import * as React from 'react';
import { ComponentClass, FC, ReactElement } from 'react';
import clsx from 'clsx';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { useTranslation } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';
import NavbarItemList from './NavbarItemList';
import { routes } from '../../Routes';
import { RouteComponentProps } from 'react-router';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useSelector } from 'react-redux';
import { IAppState } from '../../store/Store';
import { TAuthState } from '../../types/Auth';
import { useUser } from '../../hooks/useUser';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';

interface INavbarProps extends RouteComponentProps<any> {
  visible: boolean;
  text: string | ReactElement<any>;
  showBackButton?: boolean;
  hideAccountMenu?: boolean;
  customIcon?: ComponentClass | FC;
  isFrontpage?: boolean;
  disableLogoLink?: boolean;
}

const drawerWidth = 300;

const useStyles = makeStyles((theme: Theme) => ({
    root: {
      flexGrow: 1,
      color: 'white',
      paddingBottom: theme.spacing(2),
    },
    menuButton: {
      color: 'white',
      [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(2),
      },
    },
    title: {
      flexGrow: 1,
      color: 'white',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'block',
      overflow: 'hidden',
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      marginRight: theme.spacing(2),
    },
    logo: {
      maxHeight: 32,
      maxWidth: 120,
      objectFit: 'contain',
      objectPosition: 'left center',
      display: 'block',
    },
    profileIcon: {
      color: 'white',
    },
    appBar: {
      paddingTop: 'env(safe-area-inset-top)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)',
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    hide: {
      display: 'none',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
      paddingTop: 'env(safe-area-inset-top)',
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
      justifyContent: 'flex-end',
    },
    icon: {
      minWidth: '35px',
    },
  }));

const Navbar: React.FC<INavbarProps> = (props): JSX.Element => {
  const classes = useStyles({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const profileDropdownOpen = Boolean(anchorEl);
  const { t } = useTranslation();
  const { isUserAnonymous } = useUser();
  const profilePhotoUrl = useSelector<IAppState, string | null | undefined>(
    (state: { authState: TAuthState }) => state.authState.data?.profilePhotoUrl
  );
  const profilePhotoSrc = useAuthenticatedImage(profilePhotoUrl);
  const logoUrl = useSelector<IAppState, string | null | undefined>(
    (state: { authState: TAuthState }) => state.authState.settings?.logoUrl
  );
  const logoSrc = useAuthenticatedImage(logoUrl);
  const topBarColorStart = useSelector<IAppState, string | undefined>(
    (state: { authState: TAuthState }) => state.authState.settings?.topBarColorStart
  );
  const topBarColorEnd = useSelector<IAppState, string | undefined>(
    (state: { authState: TAuthState }) => state.authState.settings?.topBarColorEnd
  );
  const topBarGradient = `linear-gradient(90deg, ${topBarColorStart ?? '#0366a8'} 0%, ${topBarColorEnd ?? '#3cb0fd'} 100%)`;
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      {props.visible && (
        <div className={classes.root}>
          <AppBar
            position='fixed'
            style={{ background: topBarGradient }}
            className={clsx(classes.appBar, {
              [classes.appBarShift]: drawerOpen,
            })}
          >
            <Toolbar>
              {props.customIcon ? (
                <IconButton color='inherit' edge='start' className={classes.menuButton} size="large">
                  {React.createElement(props.customIcon)}
                </IconButton>
              ) : !props.showBackButton ? (
                <>
                  <IconButton
                    color='inherit'
                    aria-label='open drawer'
                    onClick={handleDrawerOpen}
                    edge='start'
                    className={clsx(
                      classes.menuButton,
                      drawerOpen && classes.hide
                    )}
                    size="large">
                    <MenuIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  color='inherit'
                  aria-label='open drawer'
                  onClick={() => props.history.goBack()}
                  edge='start'
                  className={clsx(
                    classes.menuButton,
                    drawerOpen && classes.hide
                  )}
                  size="large">
                  <KeyboardArrowLeftIcon fontSize={'large'} />
                </IconButton>
              )}

              {logoSrc && !drawerOpen && (
                props.disableLogoLink ? (
                  <span className={classes.logoLink}>
                    <img src={logoSrc} alt='' className={classes.logo} />
                  </span>
                ) : (
                  <Link to={routes.home} className={classes.logoLink}>
                    <img src={logoSrc} alt='' className={classes.logo} />
                  </Link>
                )
              )}

              <Typography variant='h6' className={classes.title}>
                {drawerOpen || (props.isFrontpage && logoUrl !== null) ? '' : props.text}
              </Typography>

              {!props.hideAccountMenu && (
                <div>
                  <IconButton
                    aria-label='account of current user'
                    aria-controls='menu-appbar'
                    aria-haspopup='true'
                    onClick={handleMenu}
                    color='default'
                    className={classes.profileIcon}
                    size="large">
                    {profilePhotoSrc ? (
                      <Avatar src={profilePhotoSrc} sx={{ width: 32, height: 32 }} />
                    ) : (
                      <AccountCircle />
                    )}
                  </IconButton>

                  <Menu
                    id='menu-appbar'
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={profileDropdownOpen}
                    onClose={handleClose}
                  >
                    {!isUserAnonymous() ? (
                      <div>
                        <MenuItem
                          component={Link}
                          to={routes.editProfile}
                          onClick={handleClose}
                        >
                          <ListItemIcon className={classes.icon}>
                            <PersonIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={t('navbar.edit.profile.link')}
                          />
                        </MenuItem>
                        <MenuItem
                          onClick={() => props.history.push(routes.logout)}
                        >
                          <ListItemIcon className={classes.icon}>
                            <LockIcon />
                          </ListItemIcon>
                          <ListItemText primary={t('navbar.logout.link')} />
                        </MenuItem>
                      </div>
                    ) : (
                      <MenuItem
                        onClick={() => props.history.push(routes.login)}
                      >
                        <ListItemIcon className={classes.icon}>
                          <LockIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('auth.login.submit')} />
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              )}
            </Toolbar>
          </AppBar>
          <Drawer
            className={classes.drawer}
            variant='persistent'
            anchor='left'
            open={drawerOpen}
            classes={{
              paper: classes.drawerPaper,
            }}
          >
            <div className={classes.drawerHeader}>
              <IconButton onClick={handleDrawerClose} size="large">
                <CloseIcon />
              </IconButton>
            </div>
            <NavbarItemList close={handleDrawerClose} />
          </Drawer>
        </div>
      )}
    </>
  );
};

export default withRouter(React.memo(Navbar));
